import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { FETCH_ALL_MESSAGES } from '@/hooks/useFetchAllRows'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { failureError, invalidError, notice } from '@/components/pbo/notice'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, scopedShipperCode, selectableShippers, ShipperScope, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getShipperListV2, SHIPPER_TYPES } from '@/services/shipper/apis'
import { ShipperItem } from '@/services/shipper/types'
import { SHIPPER_NAMES_MAX_SIZE } from '@/services/shipper/shipperNamesSize'
import { useFeatureFlagString } from '@/services/feature/useFeatureQueries'
import { useGetShipperNames } from '@/services/shipper/useShipperQueries'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { buildInventoryStatusesExcelUrl } from '@/services/inventory/inventoryStatus/apis'
import {
  inventoryStatusQueryKeys,
  useGetGoodsCodeOptions,
  useGetInventoryStatuses,
  useGetWarehouseNameOptions,
} from '@/services/inventory/inventoryStatus/useInventoryStatusQueries'
import { GetInventoryStatusesReq } from '@/services/inventory/inventoryStatus/types'
import { IvlInventoryRequest, IvlInventoryResult } from '@/services/inventory/inventoryStatus/ivlTypes'
import { buildIvlInventoryExcelUrl } from '@/services/inventory/inventoryStatus/ivlApis'
import { IVL_INVENTORY_COUNT_CAP, IVL_INVENTORY_EXPORT_LIMIT, IVL_INVENTORY_PAGE_SIZE } from '@/services/inventory/inventoryStatus/ivlConstants'
import { useGetInventoryWarehouseOptions, useGetIvlInventoryStatuses } from '@/services/inventory/inventoryStatus/useIvlInventoryQueries'
import {
  DEFAULT_LOGISTICS_SERVICE_TYPE,
  EXCEL_ROW_LIMIT,
  FIELD_NAMES,
  MESSAGES,
  PRODUCT_FILTER_SHIPPER_TYPES,
  REASON_DETAIL_MAX_LENGTH,
  REASON_DETAIL_MIN_LENGTH,
  SHIPPER_NAME_ALL,
  SKU_ID_TRANSITION_FLAG_KEY,
  ST02_VIEW_FIELD,
  ST02_VIEW_SKU_ATTRIBUTE,
  WAREHOUSE_ALL,
} from '@/pages/inventory/inventoryStatus/constants'
import { hasIvlInventoryFilter, toInventoryStatusesReq, toIvlInventoryRequest } from '@/pages/inventory/inventoryStatus/filterToRequest'
import { fromSearchQuery, InventoryStatusFormValues, InventoryStatusSearchQuery, toSearchQuery } from '@/pages/inventory/inventoryStatus/searchQuery'
import InventoryStatusPageView, { type Option, type Row, type Wiring } from './handoff/page.generated'
import { inventoryEmptyMessage, inventoryErrorMessage, ivlCountDisplay, IvlInventoryFeedback, toIvlColumns, toIvlViewRows } from './ivlPresentation'
import { useIvlInventoryDownload } from './hooks/useIvlInventoryDownload'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/** 화주유형 옵션 — 고정 목록이라 모듈에서 한 번만 만든다(슬롯 안에서 map 하면 렌더마다 새 배열/새 객체다). */
const SHIPPER_TYPE_OPTIONS: Option[] = SHIPPER_TYPES.map((t) => ({ value: t, label: t }))

const EMPTY_REQ: GetInventoryStatusesReq = {}
const EMPTY_ROWS: Row[] = []
const EMPTY_OPTIONS: Option[] = []

/** 첫 페이지가 비고 끝났더라도 실패·미확인 결과는 전체 0건으로 단정하지 않는다. */
const ivlExportHasNoRows = (result: IvlInventoryResult): boolean => result.pageInfo.totalCount === 0 || (
  result.pageInfo.totalCount == null && result.pageInfo.page === 0 && result.pageInfo.hasMore === false
  && result.items.length === 0 && result.failures.length === 0 && result.summary.complete === true
  && result.summary.failureCount === 0 && result.summary.unresolvedSourceCount === 0
)

/** 표시용 건수와 다운로드 안전성은 별개다. 새 응답의 정확도가 불명이면 보수적으로 차단한다. */
const ivlExportCountError = ({ totalCount, countCapped, hasMore, exportLimitEnforced }: IvlInventoryResult['pageInfo']): string | undefined => {
  if (totalCount != null && totalCount >= IVL_INVENTORY_EXPORT_LIMIT) return MESSAGES.ivlExportLimitExceeded
  if (exportLimitEnforced === true) return undefined
  // 서버 상한 보장이 없으면 새 응답의 countCapped null·누락도 정확도 불명으로 차단한다.
  if (totalCount == null || countCapped === true || (typeof hasMore === 'boolean' && countCapped !== false)) return MESSAGES.ivlExportCountUnknown
  return undefined
}

/**
 * API 행(camelCase) → 표 행(UPPER_SNAKE). 생성 뷰의 컬럼은 화면 설계(데모)의 키를 쓴다 —
 * 변환 없이 넘기면 표가 통째로 빈다. 원본 키도 남긴다(배선은 계속 camelCase 로 읽는다).
 */
/**
 * 상품코드 헬퍼 후보 → 모달 표 행. 훅은 (itemCode·brandName·itemName) 을 주는데 모달 표 컬럼은
 *  화면 설계 키(BRD_NM·GDS_CD·GDS_NM·BARCODE)다 — 이름을 바꿔 넘기지 않으면 **표가 통째로 빈다**
 *  (고른 값도 빈 문자열로 인풋에 들어간다). 2026-09-21 moms-fe #791 봇 리뷰 Critical.
 */
const toGoodsRow = (o: { itemCode?: string | null; brandName?: string | null; itemName?: string | null }): Record<string, unknown> => ({
  ...o,
  BRD_NM: o.brandName ?? '',
  GDS_CD: o.itemCode ?? '',
  GDS_NM: o.itemName ?? '',
})

const toViewRow = (r: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...r }
  for (const [k, v] of Object.entries(r)) {
    const snake = k.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    out[snake.toUpperCase()] = v // totalQty → TOTAL_QTY
    // 밑줄 없는 컬럼도 있다(totalQty → TOTALQTY · availQty → AVAILQTY). 한 벌만 내보내면 그 컬럼이 통째로 빈다.
    out[k.toUpperCase()] = v
    // 이 표의 컬럼 중 셋(service_type·shipper_name·option_code)은 **소문자 스네이크**다 — 나머지는 UPPER_SNAKE 인데
    //  이 셋만 다르다. 대문자만 내보내면 그 칸이 늘 빈칸으로 보인다(2026-09-21 moms-fe #791 봇 리뷰 Critical).
    //  ⚠ 다만 `option_code` 는 **BE 응답에 없다**(InventoryStatusItem 에 optionCode 없음) — 옛 구현도 "1.0 SELECT 에도
    //   없고 항상 공란인 죽은 컬럼" 이라며 뺐다. 서치헬퍼 모달의 BARCODE 도 같다(GoodsCodeOption 이 안 준다).
    //   이 둘은 이 줄과 무관하게 빈칸이다. 데모에서 그 컬럼을 빼야 사라진다(#791 리뷰 · 데모 소관).
    //  TODO(데모): 생성본 COLUMNS 의 키를 UPPER_SNAKE 로 통일하면 이 줄은 지운다.
    out[snake.toLowerCase()] = v // serviceType → service_type
  }
  return out
}

/** 조회영역 다건 칸 → 문자열 배열. 뷰는 배열을 주고, URL 복원은 1건일 때 문자열을 준다. */
const asList = (v: unknown): string[] => {
  const raw = Array.isArray(v) ? v : v == null || v === '' ? [] : [v]
  return raw.map((x) => String(x).trim()).filter(Boolean)
}

/** 조회영역 단건 칸 → 문자열. 뷰는 화주유형·화주명을 **배열 한 칸**으로 준다(멀티 컨트롤 규약). */
/**
 * `unknown` 값 → 화면 문자열. 조회영역·모달 폼 버스는 값을 `unknown` 으로 준다.
 *  `String(v ?? '')` 로 바로 찍으면 배열은 `"a,b"`, 객체는 `"[object Object]"` 가 **조용히** 실린다
 *  (moms-fe #803 Sonar S6551). 배열은 첫 칸을 쓰고(멀티 컨트롤 규약), 그 밖의 객체는 빈 값으로 둔다.
 */
const asText = (v: unknown): string => {
  const x = Array.isArray(v) ? v[0] : v
  return typeof x === 'string' || typeof x === 'number' ? String(x).trim() : ''
}

/**
 * 잠금 계정의 화주명 값을 고를 수 있는 화주명으로 맞춘다([scopedShipperCode]). 이 화면은 화주명으로 조회한다(`sprNm`).
 *  고를 수 있는 화주면 그대로, 아니면 기본 화주다 ([defaultShipper], LOGISPRD-15610, LOGISPRD-15719)
 */
const scopedShipperName = (scope: ShipperScope, name: string): string => {
  const selectable = selectableShippers(scope)
  // 이름이 없거나 범위 밖이면 공용 코드 검증의 기본 화주 폴백을 그대로 따른다.
  const code = scopedShipperCode(scope, selectable.find((s) => s.shipperName === name)?.shipperCode)
  return selectable.find((s) => s.shipperCode === code)?.shipperName ?? defaultShipper(scope).shipperName
}

/**
 * ST09 재고현황. 생성 뷰에 조회 조건, 응답, 다운로드 동작을 연결한다.
 * IVL 모드는 서버 페이지와 전체 요약을 사용한다. OFF 모드는 기존 이름 조회를 유지한다.
 */
function InventoryStatus({ ivlEnabled = false, coMingle = false }: { ivlEnabled?: boolean; coMingle?: boolean }) {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<InventoryStatusSearchQuery>()
  /*
   * 화주 잠금(LOGISPRD-14653) — 통합 운영 화주(FREE_SHIPPERS) 외 계정은 화주명 옵션을 자기 화주 하나로 줄이고,
   *  복원·조회·엑셀 조건의 화주명도 자기 화주로 강제한다(1.0 FilterBar 규칙). 화주유형 기본값은 '전체' 로 비운다 —
   *  기본 M1P 를 남기면 다른 유형의 화주 계정은 유형↔화주명 대조에 걸려 진입하자마자 조회를 못 한다.
   *  ⚠ 생성본 `filterDisabled` 슬롯은 쓰지 않는다 — 생성본은 잠긴 키의 **값을 지우고**(page.generated 'filterDisabled' effect)
   *   화주명 셀렉트에 disabled 를 묶지 않아, 보내면 진입 직후 화주명 칸이 비어 보인다(독립 리뷰 2026-10-10).
   */
  const scopeRef = useRef<ShipperScope>()
  // 재고통합 ON 이면 잠긴 29CM·INOOINT 가 MUSINSA 도 고른다(LOGISPRD-15610). coMingle 은 진입 시점 값이다(InventoryStatusRoute).
  scopeRef.current ??= withCoMingle(shipperScopeOf(AuthStorage.user.get()), coMingle)
  const scope = scopeRef.current
  /** 진입·초기화의 화주 기본값 — 재고통합 대상 계정은 MUSINSA (LOGISPRD-15719) */
  const baseShipper = defaultShipper(scope)
  /** 잠금 계정이 고를 수 있는 화주명. 자기 화주가 첫 번째다 */
  const selectableNames = useMemo(() => (scope.locked ? selectableShippers(scope).map((s) => s.shipperName) : []), [scope])
  // 잠금 옵션은 값이 고정이라 한 번만 만든다(옵션 슬롯은 매 렌더 불린다 — SHIPPER_TYPE_OPTIONS 와 같은 이유).
  const lockedShipperNameOptions = useMemo<Option[]>(() => selectableNames.map((n) => ({ value: n, label: n })), [selectableNames])
  /*
   * 잠금 계정의 자기 유형(LOGISPRD-14949) — 세션에는 화주명·코드만 있고 유형이 없다. 고를 수 있는 화주만 받아 logisticsServiceType 을 읽는다(GR08 준용).
   *  재고통합 ON 이면 MUSINSA 도 같이 받는다 — MUSINSA 를 고르면 화주유형이 MUSINSA 의 유형이 된다(LOGISPRD-15610).
   *  목록이 오기 전엔 모른다 — 화주유형 칸은 비어 있다가 filterFallback 이 채운다.
   */
  const { data: ownShippers } = useQuery<ShipperItem[]>({
    meta: { errorToast: true },
    queryKey: inventoryStatusQueryKeys.ownShipper(selectableNames.join(',')),
    queryFn: async (): Promise<ShipperItem[]> =>
      (await getShipperListV2({ shipperNames: selectableNames, logisticsServiceTypes: [], page: 0, size: SHIPPER_NAMES_MAX_SIZE }))?.data?.items ?? [],
    enabled: scope.locked,
    staleTime: API_COMMON_CACHE_TIME,
  })
  /** 잠금 계정이 고를 수 있는 화주명 → 그 화주의 유형. 목록이 오기 전엔 비어 있다 */
  const serviceTypeByName = useMemo(
    () => new Map(selectableShippers(scope).map((s) => [s.shipperName, ownShippers?.find((o) => o.shipperCode === s.shipperCode)?.logisticsServiceType || undefined])),
    [scope, ownShippers],
  )
  const baseServiceType = scope.locked ? serviceTypeByName.get(baseShipper.shipperName) : undefined
  // 잠금 계정의 화주유형 옵션 = 고른 화주의 유형 하나. 옵션 슬롯은 매 렌더 불리고 뷰의 filterFallback effect 가 옵션 배열 참조를 의존성으로 보므로 화주명별로 참조를 고정한다.
  const lockedServiceTypeOptions = useMemo(
    () => new Map(selectableNames.map((n): [string, Option[]] => {
      const t = serviceTypeByName.get(n)
      return [n, t ? [{ value: t, label: t }] : EMPTY_OPTIONS]
    })),
    [selectableNames, serviceTypeByName],
  )
  /** 잠금 계정이 지금 고른 화주명의 유형 */
  const lockedServiceTypeOf = useCallback((filters?: Record<string, unknown>) => serviceTypeByName.get(scopedShipperName(scope, asText(filters?.shipper_name))), [scope, serviceTypeByName])
  /** 화주를 고를 수 있는 잠금 계정의 화주유형 칸에 고른 화주와 다른 유형이 남아 있는가 */
  const isStaleServiceType = (filters: Record<string, unknown>): boolean => {
    const serviceType = scope.locked && canSelectShipper(scope) ? lockedServiceTypeOf(filters) : undefined
    return !!serviceType && asList(filters.service_type).length > 0 && asText(filters.service_type) !== serviceType
  }

  /**
   * 남겨 둔 조회 조건(URL) → 폼 값. 진입 시 입력란만 복원하고 조회는 하지 않는다.
   *  센터는 모드별 키를 쓴다(IVL = warehouseCode · OFF = whNm — 아래 filterInitial 과 같은 규칙).
   *  잠금 계정은 남은 조건의 화주명을 자기 화주로 덮는다.
   */
  const [restoredValues] = useState<InventoryStatusFormValues | undefined>(() => {
    const q = parsedQs as Partial<InventoryStatusSearchQuery>
    const query = { ...q, whNm: ivlEnabled ? q.warehouseCode : q.whNm, skuIds: ivlEnabled ? q.skuIds : undefined }
    const values = fromSearchQuery(query, {})
    if (!Object.values(values).some((v) => v !== undefined)) return undefined
    // 잠금 계정은 URL 의 화주유형도 버린다 — 칸이 잠겨 있어 고칠 수 없고, 자기 유형은 화주 목록이 온 뒤 filterFallback 이 채운다(LOGISPRD-14949).
    return scope.locked ? { ...values, [FIELD_NAMES.shipperNames]: scopedShipperName(scope, asText(values[FIELD_NAMES.shipperNames])), [FIELD_NAMES.logisticsServiceTypes]: undefined } : values
  })
  /*
   * [조회] 전에는 적용 조건을 비운다. 복원된 입력값은 버튼 검증을 거친 뒤 적용한다.
   */
  const [queryParams, setQueryParams] = useState<GetInventoryStatusesReq>(EMPTY_REQ)
  const [ivlParams, setIvlParams] = useState<IvlInventoryRequest | null>(null)
  const ivlQuery = useGetIvlInventoryStatuses(ivlEnabled ? ivlParams : null)
  const inventoryWarehouses = useGetInventoryWarehouseOptions(ivlEnabled)
  const ivlDownload = useIvlInventoryDownload()
  const ivlResult = ivlQuery.isError ? undefined : ivlQuery.data
  const ivlSearchKey = ivlParams ? JSON.stringify({ ...ivlParams, page: 0 }) : undefined
  const [lastIvlPage, setLastIvlPage] = useState<{ key: string; info: IvlInventoryResult['pageInfo'] }>()
  useEffect(() => {
    if (!ivlSearchKey) setLastIvlPage(undefined)
    else if (ivlResult) setLastIvlPage({ key: ivlSearchKey, info: ivlResult.pageInfo })
  }, [ivlSearchKey, ivlResult])
  const lastIvlPageInfo = lastIvlPage && lastIvlPage.key === ivlSearchKey ? lastIvlPage.info : undefined
  const [ivlTruncationCheck, setIvlTruncationCheck] = useState(false)
  // Spec: docs/spec/inventory/SPEC-inventory-ivl-count-cap-A.yaml#ivl-capped-search-shows-truncation-notice
  useEffect(() => {
    if (!ivlTruncationCheck || ivlQuery.isFetching) return
    if (!ivlQuery.isError && !ivlResult) return
    setIvlTruncationCheck(false)
    if (ivlResult && typeof ivlResult.pageInfo.hasMore !== 'boolean' && ivlResult.pageInfo.totalCount != null && ivlResult.pageInfo.totalCount >= IVL_INVENTORY_COUNT_CAP) notice.failure(FETCH_ALL_MESSAGES.truncated)
  }, [ivlTruncationCheck, ivlQuery.isFetching, ivlQuery.isError, ivlResult])

  /*
   * OFF 모드 전량 조회(LOGISPRD-15272) — [조회]로 적용된 조건이 있을 때만 활성화한다.
   */
  const legacy = useGetInventoryStatuses(queryParams, !ivlEnabled && queryParams !== EMPTY_REQ)
  const queryClient = useQueryClient()
  const items = legacy.rows
  const { isLoading: legacyDownloading, onDownloadExcel } = useExcelDownload({ fileNamePrefix: '재고현황' })
  // 표 loading — OFF 는 첫 구간·나머지 구간·재조회를 받는 동안 막는다(LOGISPRD-15390).
  const isFetching = ivlEnabled ? ivlQuery.isFetching : legacy.isFirstLoading || legacy.isFetchingRest
  const isDownloading = ivlEnabled ? ivlDownload.isDownloading : legacyDownloading
  const { options: legacyWarehouses } = useGetWarehouseNameOptions(!ivlEnabled)
  const warehouseOptions = ivlEnabled ? inventoryWarehouses.data ?? [] : legacyWarehouses

  // 사유코드 콤보 — 옛 모달이 쓰던 공용 콤보 그대로(코드/명 배열이 짝을 이룬다. 명이 비면 코드로 대체).
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code: string, idx: number) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [exportCombo],
  )

  /*
   * 조회영역 값에 딸린 조회들 — 뷰는 값이 바뀌었다고 알려 주는 슬롯이 없지만, 표·옵션 슬롯이 매 렌더
   *  현재 조회영역 값을 인자로 준다. 거기서 필요한 것만 뽑아 상태로 올린다(값이 실제로 바뀔 때만).
   *  렌더 중 setState 는 금지라 마이크로태스크로 미룬다.
   */
  // 조회영역에서 **실제로 소비되는 값만** 담는다 — 안 쓰는 필드를 넣으면 그 값이 바뀔 때마다
  //  쓸데없이 다시 그린다(화주명 드롭다운을 건드릴 때마다 — 2026-09-21 moms-fe #791 리뷰).
  //  재고통합 대상의 선택 화주는 상품코드 서치헬퍼도 함께 사용한다.
  const [derived, setDerived] = useState({ serviceType: DEFAULT_LOGISTICS_SERVICE_TYPE, shipperCode: baseShipper.shipperCode })
  const derivedRef = useRef(derived)
  const syncFromFilters = useCallback((filters?: Record<string, unknown>) => {
    if (!filters) return
    const shipperCode = scope.locked && canSelectShipper(scope)
      ? scopedShipperCode(scope, selectableShippers(scope).find((s) => s.shipperName === asText(filters.shipper_name))?.shipperCode)
      : scope.shipperCode
    const next = { serviceType: asText(filters.service_type) || DEFAULT_LOGISTICS_SERVICE_TYPE, shipperCode }
    const cur = derivedRef.current
    if (next.serviceType === cur.serviceType && next.shipperCode === cur.shipperCode) return
    derivedRef.current = next
    queueMicrotask(() => setDerived(next))
  }, [scope])

  /** 화주명 목록은 **고른 화주유형**에 딸린다(옛 필터의 useGetShipperNames 그대로). */
  const { data: shipperNames, isFetching: isShipperNamesFetching } = useGetShipperNames({
    logisticsServiceTypes: derived.serviceType ? [derived.serviceType] : [],
  })
  // 조회 검증이 읽는다 — 슬롯은 렌더 밖에서도 불리므로 최신 목록을 ref 로 들고 간다.
  const shipperNamesRef = useRef<string[]>([])
  shipperNamesRef.current = shipperNames ?? []
  /*
   * 목록이 **오는 중**인지도 같이 들고 간다 — 화주유형을 바꾸면 쿼리 키가 갈려 목록이 잠깐 빈다
   *  (이 훅엔 placeholderData 가 없다). 그 구간에서 아래 가드의 `names.length > 0` 이 false 라
   *  검증이 통째로 건너뛰어져, 옛 유형의 화주명 + 새 유형이 그대로 BE 로 나갔다(moms-fe #791 리뷰).
   */
  const shipperNamesLoadingRef = useRef(false)
  shipperNamesLoadingRef.current = isShipperNamesFetching

  /*
   * 상품코드 서치헬퍼 후보 — 조건은 **모달 안 [조회]를 누른 시점의 값**(브랜드명·상품명)이다(옛
   *  GoodsCodeSearchHelperModal·1.0 헬퍼 동치: 입력만으로는 받지 않는다). 뷰가 [조회]·엔터 때 적용 조건을
   *  `wiring.rows.ROWS2` 의 filters 로 준다(__applied = 눌렀음 · 열 때·[초기화] 때 비운다).
   *  재고통합 대상은 조회영역의 화주명을 코드로 바꿔 고른 화주로 받는다. 그 밖은 세션 화주를 유지한다.
   */
  const [helperOpen, setHelperOpen] = useState(false)
  const [gdsQuery, setGdsQuery] = useState({ applied: false, brand: '', item: '' })
  const gdsQueryRef = useRef(gdsQuery)
  /** 마지막으로 본 적용 조건 객체 — [조회]를 누른 순간을 가린다(같은 값 재조회도 안내). */
  const gdsAppliedObjRef = useRef<unknown>(undefined)
  /** 뷰가 매 렌더 주는 적용 조건을 상태로 올린다 — 바뀔 때만(렌더 중 setState 를 피해 마이크로태스크로). */
  const syncGdsQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = { applied: Boolean(filters?.__applied), brand: asText(filters?.BRD_NM), item: asText(filters?.GDS_NM) }
    // [조회]마다 뷰가 새 적용 조건 객체를 준다(상태라 다시 그려도 같은 객체) — 같은 값으로 다시 눌러도 다시 안내하려고 객체로 판정한다.
    const pressed = filters !== gdsAppliedObjRef.current && next.applied
    gdsAppliedObjRef.current = filters
    if (pressed) {
      queueMicrotask(() => {
        // 조건 없이 누르면 받지 않는다(아래 enabled) — 말없이 0건이면 고장처럼 보이므로 알린다.
        if (!next.brand && !next.item) notice.invalid(MESSAGES.goodsHelperConditionRequired)
      })
    }
    const cur = gdsQueryRef.current
    if (next.applied === cur.applied && next.brand === cur.brand && next.item === cur.item) return
    gdsQueryRef.current = next
    queueMicrotask(() => setGdsQuery(next))
  }, [])
  const helperParams = useMemo(
    () => ({
      shipperCode: scope.locked && canSelectShipper(scope) ? derived.shipperCode : AuthStorage.user.get().SPR_NM ?? undefined,
      brandName: gdsQuery.brand || undefined,
      itemName: gdsQuery.item || undefined,
    }),
    [gdsQuery, derived.shipperCode, scope],
  )
  /*
   * 상품코드 후보는 **모달이 열려 있고 [조회]를 눌렀고 브랜드·상품명 중 하나가 채워졌을 때만** 받는다 — goods-codes 는 무페이징 distinct 라
   *  대형 화주를 조건 없이 조회하면 응답이 수십 MB 가 되어 브라우저가 멎는다(옛 구현 주석 · dev 실기동 확인).
   *  shipperCode 만 조건으로 두면 화면에 들어오자마자 그 요청이 나간다.
   */
  const { items: goodsOptions } = useGetGoodsCodeOptions(
    helperParams,
    Boolean(helperParams.shipperCode) && (Boolean(helperParams.brandName) || Boolean(helperParams.itemName)) && helperOpen && gdsQuery.applied,
  )

  /*
   * 상품정보 원천 FF(LOGISPRD-14186) — `SKU_ATTRIBUTE` 면 BE 가 브랜드명·스타일넘버를 `sku_attribute` 에서 찾는다.
   *  그때 화주유형이 MWP·MLT 가 아니면 두 조회조건을 잠그고 값을 비운다. 조회 실패·로딩 중은 빈 값이라 현행 그대로다.
   *  슬롯은 렌더 밖([조회])에서도 불리므로 최신 값을 ref 로 들고 간다.
   */
  const { data: st02View } = useFeatureFlagString(SKU_ID_TRANSITION_FLAG_KEY, ST02_VIEW_FIELD)
  const st02ViewRef = useRef('')
  st02ViewRef.current = st02View ?? ''
  const isProductFilterDisabled = useCallback(
    (filters?: Record<string, unknown>) =>
      !ivlEnabled && st02ViewRef.current === ST02_VIEW_SKU_ATTRIBUTE && !PRODUCT_FILTER_SHIPPER_TYPES.includes(asText(filters?.service_type)),
    [ivlEnabled],
  )

  const viewRows = useMemo(() => {
    if (ivlEnabled) return ivlResult ? toIvlViewRows(ivlResult) : EMPTY_ROWS
    return items.map((r) => toViewRow(r as unknown as Record<string, unknown>)) as Row[]
  }, [ivlEnabled, ivlResult, items])

  /** 조회영역 값 → 옛 폼 값. 옛 검증·변환기(toInventoryStatusesReq·toSearchQuery)를 그대로 쓰기 위한 다리. */
  const toFormValues = useCallback((filters: Record<string, unknown>): InventoryStatusFormValues | null => {
    /*
     * ── UI 가 달라져 생긴 차이: 센터명 ──
     * 옛 조회영역은 센터명이 단일 선택이었고 API `whNm` 도 단건 EQUAL(빈 값 = 전 센터)이다.
     * 새 디자인은 멀티셀렉트라 값이 `string[]` 로 올라온다('' = 전체 sentinel).
     *  · '전체' 포함 → 빈 값(전 센터) · 1개 → 그 센터
     *  · 2개 이상 → 현재 API 가 못 받는다. 첫 값만 조용히 보내면 나머지 센터가 말없이 빠지므로 막고 안내한다.
     * TODO(api): BE 가 `whNm` 다건(IN)을 받게 되면 이 분기를 지우고 배열을 그대로 싣는다.
     */
    const whRaw = filters.WH_NM
    const whAll = Array.isArray(whRaw) ? whRaw.includes(WAREHOUSE_ALL) : whRaw === WAREHOUSE_ALL
    const whPicked = asList(whRaw)
    if (whPicked.length > 1) { notice.invalid(MESSAGES.warehouseSingleOnly); return null }
    if (ivlEnabled && (whAll || whPicked.length === 0) &&
      ![filters.BRD_NM, filters.SKU_ID, filters.BARCODE, filters.STL_NO].some((value) => asList(value).length > 0) &&
      !asText(filters.GDS_CD)) {
      // LOGISPRD-16682: 전체 센터 보호는 사용자 요청에 따라 토스트 대신 확인형 알럿으로 안내한다.
      notice.rowInvalid(MESSAGES.ivlAllWarehouseConditionRequired)
      return null
    }

    // 잠금 계정은 고른 화주의 유형으로 조회한다 — 화주를 바꾼 직후 칸에 남은 이전 화주 유형을 싣지 않는다(LOGISPRD-15610)
    const logisticsServiceTypes = (scope.locked && lockedServiceTypeOf(filters)) || asText(filters.service_type)
    const shipperNamesValue = scope.locked ? scopedShipperName(scope, asText(filters.shipper_name)) : asText(filters.shipper_name)
    // 화주유형·화주명 중 하나는 필수다(1.0 FilterBar ST02_HEAD 동치).
    if (!ivlEnabled && !logisticsServiceTypes && !shipperNamesValue) { notice.invalid(MESSAGES.shipperRequired); return null }
    /*
     * 옛 조회영역은 화주유형이 바뀌면 화주명을 '전체' 로 되돌렸다(목록이 통째로 갈리므로).
     * 새 뷰에는 값 변경 알림·되돌림 슬롯이 없어 이전 유형의 화주가 남을 수 있다 —
     * 목록이 이미 와 있는데 그 안에 없는 화주면 **조회하지 않고** 다시 고르게 한다.
     * (조용히 '전체' 로 바꿔 보내면 사용자가 고른 것과 다른 결과를 보게 된다.)
     */
    const names = shipperNamesRef.current
    /*
     * ⚠️ 유형↔화주명 대조는 **유형을 실제로 골랐을 때만** 한다.
     *  화주명 단독 조회는 정상 경로인데(필수 검증은 '유형·화주명 중 하나'), 유형이 비면
     *  `syncFromFilters` 가 기본 유형(M1P)으로 폴백해 그 목록을 받는다. 그 목록에 대조하면
     *  다른 유형의 화주명이 걸려 **유효한 조회가 차단된다**(2026-09-22 #791 리뷰).
     */
    //  잠금 계정도 건너뛴다 — 두 칸이 자기 유형·자기 화주로 고정된 값이라 사용자가 고른 조합이 아니고, 세션 화주명이 목록의 문자열과 같다는 보장이 없다(LOGISPRD-14949).
    const typePicked = Boolean(asText(filters.service_type)) && !scope.locked
    // 목록이 오는 중이면 **판정할 수 없다** — 조용히 통과시키지 않고 조회를 미룬다(가드가 뚫리던 자리).
    if (typePicked && shipperNamesValue && shipperNamesLoadingRef.current) {
      notice.failure(MESSAGES.shipperNamesLoading)
      return null
    }
    if (typePicked && shipperNamesValue && names.length > 0 && !names.includes(shipperNamesValue)) {
      notice.invalid(MESSAGES.shipperNameNotInType)
      return null
    }
    // 센터명은 '전체'가 빈 문자열이라 falsy 다 — 미선택(아무것도 안 고름)만 막는다.
    if (!ivlEnabled && !whAll && whPicked.length === 0) { notice.invalid(MESSAGES.warehouseRequired); return null }

    // 잠긴 조건은 뷰가 값을 비우지만, 비우기 전에 [조회]가 눌려도 요청에 싣지 않는다.
    const productFilterDisabled = isProductFilterDisabled(filters)

    return {
      [FIELD_NAMES.logisticsServiceTypes]: logisticsServiceTypes,
      [FIELD_NAMES.shipperNames]: shipperNamesValue || SHIPPER_NAME_ALL,
      [FIELD_NAMES.whNm]: whAll ? WAREHOUSE_ALL : whPicked[0],
      [FIELD_NAMES.brandNames]: productFilterDisabled ? [] : asList(filters.BRD_NM),
      [FIELD_NAMES.gdsCd]: asText(filters.GDS_CD) || undefined,
      [FIELD_NAMES.barcodes]: asList(filters.BARCODE),
      ...(ivlEnabled ? { [FIELD_NAMES.skuIds]: asList(filters.SKU_ID) } : {}),
      [FIELD_NAMES.styleNumbers]: productFilterDisabled ? [] : asList(filters.STL_NO),
    }
  }, [ivlEnabled, isProductFilterDisabled, scope, lockedServiceTypeOf])

  const wiring: Wiring = {
    permission: usePermissionSlot('st09'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { stStatusTable: coreTableLayoutSlot('ST09_HEAD') },
    rows: {
      // IVL 모드는 실패를 포함한 서버 건수를 전달한다. OFF 모드는 뷰가 전체 목록을 나눈다.
      ROWS: (_demo, q) => {
        syncFromFilters(q.filters)
        return { rows: viewRows, loading: isFetching, ...(ivlEnabled ? { total: ivlResult ? ivlResult.pageInfo.totalCount : ivlParams ? lastIvlPageInfo?.totalCount ?? 0 : 0 } : {}) }
      },
      // 상품코드 서치헬퍼 후보 — 옛 훅이 주는 목록 그대로(모달 표는 뷰가 그린다).
      ROWS2: (_demo, q) => { syncGdsQuery(q?.filters); return goodsOptions.map(toGoodsRow) as unknown as Row[] },
    },
    actions: {
      /** [조회] — 옛 handleSearch 그대로: 검증 → 세션·URL 저장 → 조건 커밋 후 재조회. */
      search: async (filters) => {
        const values = toFormValues(filters)
        /*
         * 검증 실패 — 알럿은 toFormValues 가 띄웠다. **`false` 를 돌려야** 뷰의 후처리가 멈춘다.
         *  `return`(undefined)이면 조회를 안 했는데도 뷰가 페이저를 1페이지로 되돌리고 '목록 검색' 입력을
         *  비운다(page.generated.tsx `if (r === false) return` — 옛 구현엔 없던 커플링이다).
         *  [조회]에서 `false` 는 "성공인데 후처리 생략" 이 아니라 그 자리의 실패 신호다(확인창 _ok 는 throw).
         */
        if (!values) return false

        const ivlRequest = ivlEnabled ? toIvlInventoryRequest(values as Record<string, unknown>) : null
        // 앞단의 전체 센터 정책과 별개로, 변환된 최종 요청에 조건이 남았는지 방어적으로 확인한다.
        if (ivlRequest && !hasIvlInventoryFilter(ivlRequest)) {
          notice.invalid(MESSAGES.ivlConditionRequired)
          return false
        }

        // 마지막 조회 조건을 메뉴 세션·URL 에 남긴다(다른 메뉴 다녀와도 유지).
        const { whNm, ...commonSearch } = toSearchQuery(values)
        const searchQuery = ivlEnabled ? { ...commonSearch, warehouseCode: whNm } : toSearchQuery(values)
        MenuStorage.menuOnSession.setSearch(searchQuery)
        reload()
        replaceQuery(searchQuery)

        if (ivlRequest) {
          setIvlTruncationCheck(true)
          if (JSON.stringify(ivlParams) === JSON.stringify(ivlRequest)) {
            await ivlQuery.refetch()
          } else {
            setIvlParams(ivlRequest)
          }
          return
        }

        // [조회] 는 늘 새로 받는다 — 전량 캐시는 staleTime Infinity 라 스스로 다시 받지 않는다. 같은 조건은 refetch,
        //  새 조건은 그 키에 남은 캐시(A → B → A, gcTime 1분 안)를 지운 뒤 키를 바꿔 첫 구간부터 받는다.
        const nextParams = toInventoryStatusesReq(values as unknown as Record<string, unknown>)
        if (JSON.stringify(queryParams) === JSON.stringify(nextParams)) {
          await legacy.refetch()
        } else {
          queryClient.removeQueries({ queryKey: inventoryStatusQueryKeys.list(nextParams), exact: true })
          setQueryParams(nextParams)
        }
      },
      /** 엑셀 — 화면에 떠 있는 결과와 **같은 조건**으로 받는다(폼 현재값이 아니라 마지막 조회 조건). */
      // 확인 슬롯은 **반환값이 없다(void)** — 생성 뷰가 토스트·닫기를 한다. 예전엔 `false` 를 돌려 토스트를
      //  막았는데, 이 화면 모달엔 토스트 문구 자체가 없어 **필요 없는 값**이었다(골격 wiring.skeleton.ts 도
      //  `async () => {}` 를 정본으로 준다). false 를 남겨 두면 생성본이 그 값을 '실패' 로 읽게 바뀌는 순간
      //  모달이 안 닫힌다 — 2026-09-21 에 실제로 그렇게 깨졌다. 실패는 throw 로만 알린다.

      stStatusDownloadModal_ok: async (ctx): Promise<void> => {
        const reasonCode = typeof ctx.form?.reason === 'string' ? ctx.form.reason : ''
        const reasonDetail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
        // 사유 필수·상세사유 최소 길이 — 옛 ExcelDownloadReasonModal 의 okDisabled 검증을 그대로 옮겼다.
        //  뷰의 [확인]은 항상 눌리므로(모달 버튼 잠금 슬롯이 없다) 여기서 막고 throw 해 모달을 열어 둔다.
        if (!reasonCode) { throw invalidError(MESSAGES.excelReasonRequired) }
        if (reasonDetail.length < REASON_DETAIL_MIN_LENGTH) {
          throw invalidError(MESSAGES.excelReasonDetailTooShort)
        }
        // 상한도 옛 모달(textarea maxLength)이 막던 것이다 — 뷰에는 그 속성이 없어 여기서 막는다.
        if (reasonDetail.length > REASON_DETAIL_MAX_LENGTH) {
          throw invalidError(MESSAGES.excelReasonDetailTooLong)
        }
        // 엑셀 컬럼 구성은 조회 대상 화주가 아니라 로그인 세션 화주로 정해진다(1.0 MOMS_GRID_COLUMN_INFO 계약).
        const viewerShipperCode = AuthStorage.user.get().SPR_NM ?? ''
        if (ivlEnabled) {
          if (isFetching) throw failureError(MESSAGES.ivlBusy) // 조회 중 — 엑셀 버튼(opens)과 같은 대기 안내
          if (!ivlParams || !ivlResult) throw invalidError(MESSAGES.ivlSearchFirst)
          if (ivlExportHasNoRows(ivlResult)) throw invalidError(MESSAGES.excelNoRows)
          const countError = ivlExportCountError(ivlResult.pageInfo)
          if (countError) throw invalidError(countError)
          // 검색에서 검증·적용한 조건을 그대로 사용한다. 전체 센터도 상품 조건이 있으면 허용한다.
          try {
            await ivlDownload.download(buildIvlInventoryExcelUrl({ ...ivlParams, viewerShipperCode, reasonCode, reasonDetail }))
          } catch (error) {
            notice.failure(inventoryErrorMessage(error))
            throw error
          }
          return
        }
        // 받는 중·실패면 안내는 guardAction 이 띄운다 — throw 로 모달만 열어 둔다.
        if (!legacy.guardAction()) throw new Error('목록을 다 받지 못해 다운로드하지 않습니다.')
        onDownloadExcel(buildInventoryStatusesExcelUrl({ ...queryParams, viewerShipperCode, reasonCode, reasonDetail }))
      },
      /** 상품코드 서치헬퍼 [선택] — 고른 값을 인풋에 넣는 일은 뷰가 한다. */
      stStatusGdsSearchModal_ok: () => {},
    },
    options: {
      /** 화주유형 — 1.0 과 같은 고정 목록(서비스 상수). 값이 고정이라 모듈 상수로 한 번만 만든다(옵션 슬롯은 매 렌더 불린다). 잠금 계정은 자기 유형 하나. */
      STSTATUSSHIPPER_TYPE_OPTIONS: (_demo, filters) =>
        scope.locked ? lockedServiceTypeOptions.get(scopedShipperName(scope, asText(filters?.shipper_name))) ?? EMPTY_OPTIONS : SHIPPER_TYPE_OPTIONS,
      /**
       * 화주명 — 고른 화주유형에 딸린 목록(옛 필터와 같은 훅). '전체'(빈 값)를 앞에 둔다.
       * 유형이 바뀌면 다음 렌더의 목록이 갈리므로, 유형 값을 상태로 올려 훅을 다시 부른다.
       */
      STSTATUSSHIPPER_NAME_OPTIONS: (_demo, filters) => {
        syncFromFilters(filters)
        if (scope.locked) return lockedShipperNameOptions
        return [{ value: SHIPPER_NAME_ALL, label: '전체' }, ...(shipperNames ?? []).map((n: string) => ({ value: n, label: n }))] as Option[]
      },
      /** 센터명 — 콤보 그대로(이름순 정렬은 훅이 한다). '전체'는 뷰의 allOption 이 붙인다. */
      STSTATUSWHNM_OPTIONS: () => warehouseOptions as Option[],
      /**
       * 사유코드 — 1.0 공용 콤보(DOWNLOAD_CD). 데모 옵션은 화면 설계용 예시라 실제 감사로그 코드와 다르다.
       * 콤보가 비면(로딩·실패) 빈 목록을 준다 — 없는 코드를 고르게 두는 쪽이 더 나쁘다.
       */
      REASONRADIO_OPTIONS: () => reasonOptions as Option[],
    },
    /** 조회영역 기본값 — 화주유형 M1P · 화주명 전체 · 센터명 전체(옛 buildDefaultValues). 잠금 계정은 기본 화주([defaultShipper])와 그 유형(모르면 빈 값 — filterFallback 이 채운다). */
    filterDefaults: {
      stStatusFilterCard: (demo) => ({
        ...demo,
        service_type: scope.locked ? (baseServiceType ? [baseServiceType] : []) : [DEFAULT_LOGISTICS_SERVICE_TYPE],
        shipper_name: scope.locked ? [baseShipper.shipperName] : [],
        WH_NM: [WAREHOUSE_ALL],
      }),
    },
    /** 잠금 계정의 화주유형은 화주 목록 응답 뒤에야 정해진다 — 비어 있을 때만 고른 화주의 유형으로 채운다(값이 있는 키는 덮지 않는다). */
    filterFallback: {
      stStatusFilterCard: (current) => {
        const serviceType = scope.locked ? lockedServiceTypeOf(current) : undefined
        return serviceType && asList(current.service_type).length === 0 ? { service_type: [serviceType] } : undefined
      },
    },
    /**
     * 화주 고정 계정은 화주유형·화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 값을 비우는 filterDisabled 가 아니라 값을 유지하는 filterLocked 다.
     *  재고통합 ON 의 29CM·INOOINT 는 화주명만 연다 — 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15610)
     */
    filterLocked: scope.locked ? { service_type: true, shipper_name: !canSelectShipper(scope) } : undefined,
    /** 진입 초기값 — 세션·URL 에 남은 마지막 조회 조건을 조회영역 모양으로 되돌린다. */
    filterInitial: {
      /*
       * 복원된 입력값(restoredValues)은 [조회]할 때 검증 후 적용한다.
       *  저장 조건에 화주유형이 없으면(유형을 비우고 조회했다) 기본값 M1P 를 남기지 않고 비워 연다 —
       *  남기면 복원된 조건과 사용자가 확인한 조회 조건이 달라진다(#940 리뷰).
       */
      stStatusFilterCard: (defaults) => {
        const v = restoredValues
        if (!v) return defaults
        return {
          ...defaults,
          service_type: scope.locked ? asList(lockedServiceTypeOf({ shipper_name: v[FIELD_NAMES.shipperNames] })) : v[FIELD_NAMES.logisticsServiceTypes] ? [v[FIELD_NAMES.logisticsServiceTypes]] : [],
          ...(v[FIELD_NAMES.shipperNames] ? { shipper_name: [v[FIELD_NAMES.shipperNames]] } : {}),
          ...(v[FIELD_NAMES.whNm] !== undefined ? { WH_NM: [v[FIELD_NAMES.whNm]] } : {}),
          ...(v[FIELD_NAMES.brandNames] ? { BRD_NM: v[FIELD_NAMES.brandNames] } : {}),
          ...(v[FIELD_NAMES.gdsCd] ? { GDS_CD: v[FIELD_NAMES.gdsCd] } : {}),
          ...(v[FIELD_NAMES.barcodes] ? { BARCODE: v[FIELD_NAMES.barcodes] } : {}),
          ...(v[FIELD_NAMES.skuIds] ? { SKU_ID: v[FIELD_NAMES.skuIds] } : {}),
          ...(v[FIELD_NAMES.styleNumbers] ? { STL_NO: v[FIELD_NAMES.styleNumbers] } : {}),
        }
      },
    },
    /**
     * 브랜드명·스타일넘버 잠금 — 상품정보 원천 FF 와 화주유형으로 정한다(LOGISPRD-14186).
     *  화주유형은 잠금 계정이 화주를 바꿨을 때 이전 화주 유형이 남아 있으면 한 번 비운다 — 비우면 filterFallback 이
     *  고른 화주의 유형으로 다시 채운다. 생성본에 값이 있는 칸을 덮는 슬롯이 없어서다(LOGISPRD-15610)
     */
    filterDisabled: {
      stStatusFilterCard: (filters): Record<string, boolean> => ({
        ...(isProductFilterDisabled(filters) ? { BRD_NM: true, STL_NO: true } : {}),
        ...(isStaleServiceType(filters) ? { service_type: true } : {}),
      }),
    },
    canChangePage: ({ pager }) => !ivlEnabled || pager !== 'pager' || (!isFetching && !!ivlResult),
    useBusy: () => isDownloading,
    opens: {
      /** 엑셀 — 결과가 있어야 하고 상한을 넘으면 안 된다(옛 handleDownloadClick). */
      stStatusExcelButton: () => {
        if (ivlEnabled) {
          if (isFetching || isDownloading) { notice.failure(MESSAGES.ivlBusy); return false }
          if (!ivlParams) { notice.invalid(MESSAGES.ivlSearchFirst); return false } // 아직 조회 전 — 다운로드 확인창(ivlSearchFirst)과 같은 문구
          // 전체 센터 검색과 같은 상품 조건 정책을 적용하고, 엑셀 건수 상한은 별도로 유지한다.
          if (!ivlResult || ivlExportHasNoRows(ivlResult)) { notice.invalid(MESSAGES.excelNoRows); return false }
          // Spec: docs/spec/inventory/SPEC-inventory-ivl-count-cap-A.yaml#ivl-excel-button-blocks-at-cap
          const countError = ivlExportCountError(ivlResult.pageInfo)
          if (countError) {
            notice.invalid(countError)
            return false
          }
          return true
        }
        if (!legacy.guardAction()) return false
        if (items.length === 0) { notice.invalid(MESSAGES.excelNoRows); return false }
        if (items.length > EXCEL_ROW_LIMIT) { notice.invalid(MESSAGES.excelRowLimitExceeded); return false }
        return true
      },
    },
    /** 서치헬퍼 모달 열림 — 후보 조회를 이 신호로 게이트한다(옛 isGoodsHelperOpen). */
    onModal: (open) => {
      setHelperOpen((s) => (s === open.stStatusGdsSearchModal ? s : open.stStatusGdsSearchModal))
      // 닫히면 그 모달의 적용 조건을 비운다 — 남겨 두면 다시 열 때 뷰가 빈 조건을 주기 전 한 렌더 동안
      //  '열림 + 지난 [조회] 조건'이 되어 후보 요청이 한 번 나간다(#861 리뷰).
      if (!open.stStatusGdsSearchModal && gdsQueryRef.current.applied) { gdsQueryRef.current = { applied: false, brand: '', item: '' }; setGdsQuery({ applied: false, brand: '', item: '' }) }
    },
    /** 저장된 조건을 지운다. IVL 모드는 적용 조건과 결과도 함께 비운다. */
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      // 탭바는 reload 로만 새 조건을 읽는다 — 빼면 복귀 시 지운 조건이 입력란에 다시 채워진다.
      reload()
      replaceQuery({})
      if (ivlEnabled) setIvlParams(null)
    },
    ...(ivlEnabled ? {
      table: {
        columns: toIvlColumns,
        totalDisplay: <span>조회 대상 <b>{ivlResult ? ivlCountDisplay(ivlResult.pageInfo.totalCount) : '—'}</b></span>,
        emptyText: inventoryEmptyMessage(ivlResult, ivlParams !== null, ivlQuery.error),
        feedback: <IvlInventoryFeedback result={ivlResult} error={ivlQuery.error} warehouseError={inventoryWarehouses.error} />,
        pager: { page: (ivlParams?.page ?? 0) + 1, size: ivlParams?.size ?? IVL_INVENTORY_PAGE_SIZE },
        continuation: ivlParams && typeof (ivlResult?.pageInfo ?? lastIvlPageInfo)?.hasMore === 'boolean'
          ? { hasMore: ivlResult?.pageInfo.hasMore === true, disabled: isFetching || !ivlResult }
          : undefined,
        onPage: (page: number, size: number) => {
          if (isFetching || !ivlResult) return
          if (!Number.isInteger(page) || page < 1 || !Number.isInteger(size) || size < 1 || size > IVL_INVENTORY_PAGE_SIZE) {
            notice.invalid(MESSAGES.ivlPageInvalid)
            return
          }
          setIvlParams((current) => current ? { ...current, page: size === current.size ? page - 1 : 0, size } : null)
        },
      },
      optionalSearchFilters: true,
      skuIdFilter: true,
    } : {
      table: { emptyText: queryParams === EMPTY_REQ ? inventoryEmptyMessage() : undefined },
    }),
  }

  return <InventoryStatusPageView wiring={wiring} />
}

export default InventoryStatus
